import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { LOGO } from "@/data/exercises";

const SPACES = {
  soignant: { title: "Créer un compte soignant", sub: "Commencez à accompagner vos patients.", name: "Soignant & Patient", to: "/tableau-de-bord" },
  famille: { title: "Créer un compte aidant", sub: "Accompagnez votre proche à la maison, en famille.", name: "Aidant & Famille", to: "/famille" },
};

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const space = SPACES[params.get("espace")] || SPACES.soignant;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, params.get("espace") || "");
      navigate(SPACES[params.get("espace")]?.to || "/espaces");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F4F0E8] font-care">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-sm p-8"
        data-testid="register-form"
      >
        <div className="flex justify-center mb-6">
          <img src={LOGO} alt="Passerelle" className="h-20 w-auto" data-testid="register-logo" />
        </div>
        <h2 className="font-serif-display text-2xl font-normal mb-1" data-testid="register-title">{space.title}</h2>
        <p className="text-gray-500 mb-4">{space.sub}</p>
        <p className="mb-6 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 font-semibold" data-testid="register-trial-info">
          {`7 jours d'essai gratuit de l'espace ${space.name}, sans carte bancaire.`}
        </p>

        {error && (
          <div
            className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm"
            data-testid="register-error"
          >
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Nom</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Votre nom" className="mt-1 h-12" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr" className="mt-1 h-12" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Au moins 6 caractères"
              className="mt-1 h-12" data-testid="register-password-input" />
          </div>
        </div>

        <Button type="submit" disabled={loading}
          className="w-full h-12 mt-6 text-base bg-emerald-700 hover:bg-emerald-800 rounded-xl"
          data-testid="register-submit-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer mon compte"}
        </Button>

        <p className="text-center text-sm text-gray-500 mt-5">
          Déjà inscrit ?{" "}
          <Link to="/connexion" className="text-emerald-700 font-semibold" data-testid="go-login-link">
            Se connecter
          </Link>
        </p>
      </form>
    </div>
  );
}
